import { css } from '@emotion/core'
import { useLocation } from '@reach/router'
import { GatsbyImage } from 'gatsby-plugin-image'
import React from 'react'

import { breakpoints } from '../constants/breakPoints'
import { AuthorAvatarProps } from '../interfaces/learn'
import { Link } from './Link'

export const AuthorAvatar: React.FunctionComponent<AuthorAvatarProps> = ({
  id,
  isInSidebar = false,
  withDescription = false,
  blackBG = true,
  author: {
    authorPhoto,
    name,
    jobTitle,
    companyName = '',
    description: { description } = {},
    slug,
    authorPublications,
  },
  leftOnSmall = false,
  cssStyles = css``,
}) => {
  const [showFullDescription, setShowFullDescription] = React.useState(false)

  const location = useLocation()
  const subdirectory =
    (location.pathname.includes('blog') && 'blog/author/') ||
    (location.pathname.includes('product-learn') && 'product-learn/author/') ||
    (location.pathname.includes('templates') && 'templates/expert/') ||
    ''

  const getAuthorAvatarPublicationsTerm = (locationPathname: string) => {
    switch (true) {
      case locationPathname.includes('blog'):
      case locationPathname.includes('product-learn'):
        return 'Article'
      case locationPathname.includes('templates'):
        return 'Template'
      default:
        return 'Publication'
    }
  }

  const publicationsCount =
    authorPublications &&
    `${authorPublications.length} ${getAuthorAvatarPublicationsTerm(location.pathname)}${
      authorPublications.length === 1 ? '' : 's'
    }`

  return isInSidebar ? (
    <div id={id} css={cssStyles}>
      <div className="left">
        <Link className="opacity-on-hover" href={`/${subdirectory}${slug}/`}>
          <div>
            <GatsbyImage
              image={authorPhoto.gatsbyImageData}
              css={imageStyle(leftOnSmall)}
              alt={`airfocus author: ${name}`}
            />
            <div className={`${blackBG ? 'white' : 'black'}-text`}>
              <p className="title left" css={nameStyle}>
                {name}
              </p>
              <div className={`${blackBG ? 'white' : 'black'}-text footnote left`} css={jobStyle(leftOnSmall)}>
                {jobTitle} {companyName && `@ ${companyName}`}
              </div>
            </div>
          </div>
        </Link>
        {withDescription && description && (
          <div className={`body-2 ${blackBG ? 'white-text' : 'black-text'} ${withDescription && 'left'}`}>
            <span css={descriptionStyle(isInSidebar, showFullDescription)}>
              {description}
              <Link
                className="strong-body-2 blue-text"
                onClick={() => setShowFullDescription(true)}
                css={showMoreStyle(blackBG)}
              >
                {!showFullDescription && '...more'}
              </Link>
            </span>
          </div>
        )}
        {publicationsCount && (
          <Link className="opacity-on-hover" href={`/${subdirectory}${slug}/`}>
            <div css={publicationsCounterStyle(isInSidebar)}>{publicationsCount}</div>
          </Link>
        )}
      </div>
    </div>
  ) : (
    <div id={id} css={[authorContainerStyle, cssStyles]}>
      <Link href={`/${subdirectory}${slug}/`}>
        <div className={`center ${leftOnSmall && 'left-on-small'}`} css={headStyle(leftOnSmall)}>
          <GatsbyImage
            image={authorPhoto.gatsbyImageData}
            css={imageStyle(leftOnSmall)}
            alt={`airfocus author: ${name}`}
          />
          <div className={`${blackBG ? 'white' : 'black'}-text`}>
            <p className={`title ${`center ${leftOnSmall && 'left-on-small'}`}`} css={nameStyle}>
              {name}
            </p>
            <div
              className={`${blackBG ? 'white' : 'black'}-text footnote ${`center ${leftOnSmall && 'left-on-small'}`}`}
              css={jobStyle(leftOnSmall)}
            >
              {jobTitle} {companyName && `@ ${companyName}`}
            </div>
          </div>
        </div>
      </Link>

      {withDescription && description ? (
        <div
          className={`body-2 ${blackBG ? 'white' : 'black'}-text ${
            withDescription && `center ${leftOnSmall && 'left-on-small'}`
          }`}
          css={descriptionStyle(isInSidebar, showFullDescription)}
        >
          {description}
          <Link
            className="strong-body-2 blue-text"
            onClick={() => setShowFullDescription(true)}
            css={showMoreStyle(blackBG)}
          >
            {!showFullDescription && '...more'}
          </Link>
        </div>
      ) : (
        publicationsCount && (
          <Link className="opacity-on-hover" href={`/${subdirectory}${slug}/`}>
            <div css={publicationsCounterStyle(isInSidebar)}>{publicationsCount}</div>
          </Link>
        )
      )}
    </div>
  )
}

const authorContainerStyle = css`
  &:hover {
    p.title {
      opacity: 0.65;
    }
  }

  @media screen and (max-width: ${breakpoints.medium}px) {
    margin: 2rem auto;
  }
`

const headStyle = (leftOnSmall: boolean) => css`
  ${leftOnSmall &&
  `
    @media screen and (max-width: ${breakpoints.small}px) {
      display: flex;
      align-items: center;
      column-gap: 1rem;
      margin-bottom: 1rem;
  }
  `}
`

const imageStyle = (leftOnSmall: boolean) => css`
  margin-bottom: 1rem;
  border-radius: 50%;
  border: 1px solid #efeff0;
  max-width: 5rem;
  max-height: 5rem;
  aspect-ratio: 1;

  ${leftOnSmall &&
  `
    @media screen and (max-width: ${breakpoints.small}px) {
      max-width: 4rem;
      max-height: 4rem;
      margin-bottom: 0;
    }
  `}
`

const publicationsCounterStyle = (isInSidebar: boolean) => css`
  display: block;
  background-color: #efeff0;
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  max-width: fit-content;
  color: #000;
  margin: ${isInSidebar ? '1rem 0 0' : '0 auto 1rem'};
  font-size: 0.75rem;
  line-height: 1rem;
`

const nameStyle = css`
  transition: opacity 0.125s ease-in-out;
  margin-bottom: 0.25rem !important;
`

const jobStyle = (leftOnSmall: boolean) => css`
  margin-bottom: 0.75rem;

  ${leftOnSmall &&
  `
    @media screen and (max-width: ${breakpoints.small}px) {
      margin-bottom: 0;
    }
  `}
`

const descriptionStyle = (isInSidebar: boolean, showFullDescription: boolean) => css`
  ${!isInSidebar && 'max-width: 34rem;'};
  ${!showFullDescription &&
  `
    position: relative;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  `};
`

const showMoreStyle = (blackBG: boolean) => css`
  position: absolute;
  bottom: 0;
  right: 0;
  padding-left: 2rem;
  background: linear-gradient(
    270deg,
    rgba(${blackBG ? '7, 19, 34' : '255, 255, 255'}, 1),
    rgba(${blackBG ? '7, 19, 34, 0.99' : '255, 255, 255, 0.99'}) 60%,
    rgba(${blackBG ? '7, 19, 34, 0.80' : '255, 255, 255, 0.70'}) 80%,
    rgba(${blackBG ? '7, 19, 34' : '255, 255, 255'}, 0.1) 100%
  );

  &:hover {
    // same as #318eff with 65% opacity
    // (className opacity-on-hover does not work because it would affect the background above as well)
    color: #318effa5;
  }
`
